import { notifications } from '@mantine/notifications'
import { useCallback, useState } from 'react'
import type { AttachmentHooks } from '../attachments/hooks'
import { compressImage } from '../utils/image'
import { validateFileAge } from '../utils/fileValidation'
import type { AttachmentCategory } from './CategoryAttachments'

interface UseAttachmentUploadParams {
  category: AttachmentCategory
  propertyId: number
  allowDocuments?: boolean
  maxPhotoAgeHours?: number
  successMessage?: string
  propertyHooks: {
    useUpdate: () => {
      mutateAsync: (input: {
        id: number
        data: Record<string, unknown>
        attachments?: Array<{ id: number; action: 'add' | 'delete' }>
      }) => Promise<unknown>
    }
  }
  attachmentHooks: AttachmentHooks
}

export function useAttachmentUpload({
  category,
  propertyId,
  allowDocuments = false,
  maxPhotoAgeHours = Number(import.meta.env.VITE_MAX_PHOTO_AGE_HOURS) ||
    undefined,
  successMessage,
  propertyHooks,
  attachmentHooks,
}: UseAttachmentUploadParams) {
  const [uploading, setUploading] = useState(false)
  const createAttachment = attachmentHooks.useUploadAttachment.getMutation()
  const { mutateAsync: updateProperty } = propertyHooks.useUpdate()

  const handleUpload = useCallback(
    async (file: File, extraMetadata?: Record<string, unknown>) => {
      if (!file) return

      const isImage = file.type.startsWith('image/')

      if (!allowDocuments && !isImage) {
        notifications.show({
          title: 'Virhe',
          message: 'Vain kuvatiedostot ovat sallittuja',
          color: 'red',
        })
        return
      }

      // Validate photo age if maxPhotoAgeHours is provided
      if (!(await validateFileAge(file, maxPhotoAgeHours))) {
        return
      }

      setUploading(true)

      try {
        // Compress image if it's an image file
        const fileToUpload = await compressImage(file)

        const uploadData = await createAttachment.mutateAsync({
          filename: fileToUpload.name,
          fileSize: fileToUpload.size,
          bucket: 'private',
          metadata: {
            category,
            ...extraMetadata,
          },
        })

        const formData = new FormData()
        Object.entries(uploadData.fields || {}).forEach(([key, value]) => {
          formData.append(key, value as string)
        })
        formData.append('file', fileToUpload)

        const uploadResponse = await fetch(uploadData.uploadUrl, {
          method: 'POST',
          body: formData,
        })

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`)
        }

        await updateProperty({
          id: propertyId,
          data: {},
          attachments: [{ id: uploadData.id, action: 'add' }],
        })

        if (successMessage) {
          notifications.show({
            title: 'Onnistui',
            message: successMessage,
            color: 'green',
          })
        }
      } catch (error) {
        console.error('Upload failed:', error)
        notifications.show({
          title: 'Virhe',
          message: 'Lataus epäonnistui',
          color: 'red',
        })
      } finally {
        setUploading(false)
      }
    },
    [
      allowDocuments,
      category,
      createAttachment,
      maxPhotoAgeHours,
      propertyId,
      successMessage,
      updateProperty,
    ],
  )

  return { handleUpload, uploading }
}
